import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import Stepper from "@/components/Stepper";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/common";
import { ArrowLeft } from "lucide-react";
import PlanStep from "@/components/steps/PlanStep";
import PrepareStep from "@/components/steps/PrepareStep";
import RecordStep from "@/components/steps/RecordStep";
import ReviewStep from "@/components/steps/ReviewStep";
import ClarifyStep from "@/components/steps/ClarifyStep";
import GenerateStep from "@/components/steps/GenerateStep";
import MeetingInsights from "@/components/MeetingInsights";
import { BarChart3, Workflow } from "lucide-react";

export default function MeetingWorkspace() {
  const { pid, mid } = useParams();
  const navigate = useNavigate();
  const [active, setActive] = useState("plan");
  const [mode, setMode] = useState("workflow");

  const meetingQ = useQuery({ queryKey: ["meeting", mid], queryFn: async () => (await api.get(`/meetings/${mid}`)).data });
  const projectQ = useQuery({ queryKey: ["project", pid], queryFn: async () => (await api.get(`/projects/${pid}`)).data });
  const txQ = useQuery({ queryKey: ["transcripts", mid], queryFn: async () => (await api.get(`/meetings/${mid}/transcripts`)).data });

  const meeting = meetingQ.data;
  const done = useMemo(() => {
    const d = [];
    if (meeting?.agenda?.length) d.push("plan");
    if (meeting?.module_mapping?.length) d.push("prepare");
    if ((txQ.data || []).length) { d.push("record"); d.push("review"); }
    if (meeting?.analysis) d.push("clarify");
    return d;
  }, [meeting, txQ.data]);

  const refresh = () => { meetingQ.refetch(); txQ.refetch(); };

  if (meetingQ.isLoading || projectQ.isLoading) return <Spinner label="Loading meeting…" />;

  const props = { meeting, project: projectQ.data, transcripts: txQ.data || [], refresh, setActive, pid, mid };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/projects/${pid}`)} data-testid="back-to-project"><ArrowLeft size={18} /></Button>
        <div>
          <div className="text-[11px] font-semibold tracking-[0.18em] text-[#29abe2] uppercase">{projectQ.data?.client_name}</div>
          <h1 className="text-xl sm:text-2xl font-semibold text-foreground">{meeting?.title}</h1>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={() => setMode("workflow")} data-testid="mode-workflow"
          className={`flex items-center gap-1.5 text-sm px-3.5 py-1.5 rounded-lg border ${mode === "workflow" ? "bg-[#0f2557] text-white border-[#0f2557] dark:bg-[#29abe2] dark:border-[#29abe2]" : "hover:border-[#29abe2]"}`}><Workflow size={14} /> Workflow</button>
        <button onClick={() => setMode("insights")} data-testid="mode-insights"
          className={`flex items-center gap-1.5 text-sm px-3.5 py-1.5 rounded-lg border ${mode === "insights" ? "bg-[#0f2557] text-white border-[#0f2557] dark:bg-[#29abe2] dark:border-[#29abe2]" : "hover:border-[#29abe2]"}`}><BarChart3 size={14} /> Insights &amp; Minutes</button>
      </div>

      {mode === "insights" ? (
        <MeetingInsights meeting={meeting} mid={mid} transcripts={txQ.data || []} refresh={refresh} />
      ) : (
      <>
      <div className="rounded-xl border bg-card p-4 overflow-x-auto">
        <Stepper active={active} done={done} onSelect={setActive} />
      </div>

      <div>
        {active === "plan" && <PlanStep {...props} />}
        {active === "prepare" && <PrepareStep {...props} />}
        {active === "record" && <RecordStep {...props} />}
        {active === "review" && <ReviewStep {...props} />}
        {active === "clarify" && <ClarifyStep {...props} />}
        {active === "generate" && <GenerateStep {...props} />}
        {active === "track" && (
          <div className="rounded-xl border bg-card p-10 text-center">
            <p className="text-muted-foreground">Project tracking lives in the Project Hub.</p>
            <Button className="mt-4" onClick={() => navigate(`/projects/${pid}?tab=plan`)}>Open PM Hub</Button>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}
